import React, { useState, useEffect, useRef } from 'react';
import CountUp from 'react-countup';
import { useInView } from 'react-intersection-observer';
import Accordion from './Accordion';
import Gallery from './Gallery';


const slides = [
  {
    endValue: 13000000,
    title: 'Our commitments to protect the marine environment',
    content: '',
  },
  {
    endValue: 10000000,
    title: 'to Combat Plastic Pollution (5 years)',
    content: 'Our most significant commitment is to tackle the pressing environmental challenge of plastic pollution. The "Typhoon Project", our flagship initiative, is at the forefront of efforts to combat plastic pollution. Through field activities, coastal and underwater clean-ups, strategic partnerships, educational programs, and academic research, we aim to further protect the marine environment and prevent marine pollution.',
  },
  {
    endValue: 1500000,
    title: 'to Combat Marine Pollution in the Saronic Gulf (3 years)',
    content: 'Did you know that the Northern Saronic Gulf is one of the most degraded marine areas in Greece? By designating the Saronic Gulf as a "national experimental area", we will conduct comprehensive studies on the anthropogenic impacts, including chemical pollution, waste, and biodiversity degradation. The extensive database generated will assist in formulating policies and strategies for the restoration of the Saronic environment, where pollution has significantly increased over the past 50 years.',
  },
  {
    endValue: 1500000,
    title: 'to Continue the "Fishing for Litter" Project (5 years)',
    content: 'With the support of the fishing community, we are continuing the "Fishing for Litter" project, aimed at cleaning and protecting the seabed. The project, implemented by the Athanasios C. Laskaridis Charitable Foundation in collaboration with the environmental organization iSea, involves trawling fishermen who collect litter caught in their nets during fishing operations and dispose of it properly at designated facilities. The data collected is analyzed by our scientific team to understand the nature of the debris. From 2018 to 2023, the project involved 59 fishing boats across 18 ports in Greece.',
  },
  {
    title: 'Supporting the Marine Protected Areas Monitoring Program (2 years)',
    content: 'Technology plays a crucial role in environmental protection. The Greek Government, in collaboration with the internationally recognized non-profit organization "Global Fishing Watch", is launching a program to monitor marine protected areas using modern technologies. The Athanasios C. Laskaridis Charitable Foundation will provide monitoring tools and other technologies to support the program, alongside financial backing for the initiative.',
  },
];

const CountUpComponent = ({ end }) => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.5,
  });

  return (
    <span ref={ref}>
      {inView ? <CountUp end={end} duration={3} separator="." suffix="€" /> : "0€"}
    </span>
  );
};

const Commitments = () => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [popupContentType, setPopupContentType] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const sliderRef = useRef(null);

  const handlePopupOpen = (type) => {
    setPopupContentType(type);
    setIsPopupOpen(true);
  };

  const handlePopupClose = () => {
    setIsPopupOpen(false);
    setPopupContentType('');
  };

  const handleScroll = (e) => {
    if (isDragging) return;
  
    const sliderRect = sliderRef.current.getBoundingClientRect();
    const isCentered = sliderRect.top >= 0 && sliderRect.bottom <= window.innerHeight;
  
    if (isCentered) {
      if (currentIndex === 0) {
        // On the first slide: allow upward scroll, change slide on downward scroll
        if (e.deltaY > 0) {
          e.preventDefault(); // Prevent default downward scroll to change slides
          setCurrentIndex((prevIndex) => Math.min(prevIndex + 1, slides.length - 1));
        }
        // Allow normal upward scrolling without preventing default scroll
      } else if (currentIndex === slides.length - 1) {
        // On the last slide: allow downward scroll, prevent upward scroll to change slides
        if (e.deltaY < 0) {
          e.preventDefault();
          setCurrentIndex((prevIndex) => Math.max(prevIndex - 1, 0));
        }
      } else {
        // On other slides: prevent default scroll and change slide in both directions
        e.preventDefault();
        if (e.deltaY > 0) {
          setCurrentIndex((prevIndex) => Math.min(prevIndex + 1, slides.length - 1));
        } else if (e.deltaY < 0) {
          setCurrentIndex((prevIndex) => Math.max(prevIndex - 1, 0));
        }
      }
    }
  };
  
  useEffect(() => {
    const handleScrollEvent = (e) => {
      handleScroll(e);
    };
  
    const sliderElement = sliderRef.current;
  
    const checkIfSliderIsCentered = () => {
      const sliderRect = sliderElement.getBoundingClientRect();
      const isCentered = sliderRect.top >= 0 && sliderRect.bottom <= window.innerHeight;
  
      // Apply overflow: hidden only for slides other than the first
      if (isCentered && currentIndex !== 0 && currentIndex !== slides.length - 1) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = ''; // Allow default scrolling on the first and last slides
      }
    };
  
    if (sliderElement) {
      sliderElement.addEventListener('wheel', handleScrollEvent, { passive: false });
    }
  
    checkIfSliderIsCentered();
    window.addEventListener('resize', checkIfSliderIsCentered);
    window.addEventListener('scroll', checkIfSliderIsCentered);
  
    return () => {
      if (sliderElement) {
        sliderElement.removeEventListener('wheel', handleScrollEvent);
      }
      window.removeEventListener('resize', checkIfSliderIsCentered);
      window.removeEventListener('scroll', checkIfSliderIsCentered);
      document.body.style.overflow = '';
    };
  }, [currentIndex, isDragging, slides.length]);
  
  

  const handleTouchStart = (e) => {
    setStartX(e.touches[0].clientX);
    setIsDragging(true);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;

    const moveX = e.touches[0].clientX - startX;

    if (moveX > 100) {
      
      if (currentIndex > 0) {
        setCurrentIndex(prevIndex => Math.max(prevIndex - 1, 0));
        setStartX(e.touches[0].clientX); 
      }
    } else if (moveX < -100) {
     
      if (currentIndex < slides.length - 1) {
        setCurrentIndex(prevIndex => Math.min(prevIndex + 1, slides.length - 1));
        setStartX(e.touches[0].clientX); 
      }
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  const handleMouseDown = (e) => {
    setStartX(e.clientX);
    setIsDragging(true);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;

    const moveX = e.clientX - startX;

    if (moveX > 100) {
      
      if (currentIndex > 0) {
        setCurrentIndex(prevIndex => Math.max(prevIndex - 1, 0));
        setStartX(e.clientX);
      }
    } else if (moveX < -100) {
      
      if (currentIndex < slides.length - 1) {
        setCurrentIndex(prevIndex => Math.min(prevIndex + 1, slides.length - 1));
        setStartX(e.clientX); 
      }
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handlePaginationClick = (index) => {

    setCurrentIndex(index);
  };
  
  useEffect(() => {
    const acc = document.getElementsByClassName('accordion');
    if (acc.length > 0) {
      acc[0].classList.add('active');
      const firstPanel = acc[0].nextElementSibling;
      firstPanel.style.maxHeight = firstPanel.scrollHeight + 134 + 'px';
    }

    for (let i = 0; i < acc.length; i++) {
      acc[i].addEventListener('click', function() {
        for (let j = 0; j < acc.length; j++) {
          if (j !== i) {
            acc[j].classList.remove('active');
            const otherPanel = acc[j].nextElementSibling;
            otherPanel.style.maxHeight = null;
            otherPanel.style.padding = null;
          }
        }

        this.classList.toggle('active');
        const panel = this.nextElementSibling;
        if (panel.style.maxHeight) {
          panel.style.maxHeight = null;
          panel.style.padding = null;
        } else {
          panel.style.maxHeight = panel.scrollHeight + 134 + 'px';
        }
      });
    }
  }, []);
  
  return (
    <section className="s-sec">
      <div className="background__">
        <img src="img/vertical-aerial-shot-sea-waves-hitting-cliff.jpg" alt="Sea Waves" />
        <div className="background__grad"></div>
      </div>
      <div className="commit-wrapper">
        <h4>Our Commitments</h4>
        <div
        className="slider-container comSlider"
        ref={sliderRef}
        onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
      >
        <div
          className="slides-wrapper"
          style={{
            transform: `translateX(-${currentIndex * 100}%)`,
          }}
        >
          {slides.map((slide, index) => (
            <div
              key={index}
              className={`slide ${index === currentIndex ? 'active' : ''}`}
            >
              {slide.endValue && <CountUpComponent end={slide.endValue} />}
              <h5>{slide.title}</h5>
              <p>{slide.content}</p>
            </div>
          ))}
        </div>
        <div className="pagination">
          {slides.map((_, index) => (
            <span
              key={index}
              className={`pagination-dot ${index === currentIndex ? 'active' : ''}`}
              onClick={() => handlePaginationClick(index)}
            ></span>
          ))}
        </div>
      </div>
      </div>
      <div className="part-wrapper">
        <div className="title-wrapper">
          <h2>Our Participations in the 9th Our Ocean Conference</h2>
        </div>
          <Accordion title="Side Event: Technology for Good: How Innovation and Open Data Can Deliver Ocean Action" index="1">
            <div className="flex">
              <div className="panel-left">
                <p>The side event, “Technology for Good: How Innovation and Open Data Can Deliver Ocean Action,” highlighted the pivotal role of technology in ocean protection. It underscored the importance of access to open data for fisheries management. The Greek Government, in collaboration with “Global Fishing Watch,” announced a joint program to monitor marine protected areas using advanced technologies. The Athanasios C. Laskaridis Charitable Foundation will contribute to the program’s implementation and financially support the collaboration.</p>
                <ul> Speakers:
                  <li><strong>Tony Long</strong>, CEO of Global Fishing Watch</li>
                  <li><strong>Rocio Parra Cortes</strong>, Head of the Legal Division, Undersecretariat of Fisheries and Aquaculture of Chile</li>
                  <li><strong>Jose Julios Casas</strong>, Technical Secretary, Corredor Marino del Pacifico Este Tropical (CMAR)</li>
                  <li><strong>Dr. Khallahi Brahim</strong>, Permanent Secretary, Sub-Regional Fisheries Commission</li>
                  <li><strong>Dr. Petros Varelidis</strong>, Secretary General, Greek Ministry of Environment and Energy</li>
                  <li><strong>Evi Lazou-Laskaridis</strong>, Chairman, Athanasios C. Laskaridis Charitable Foundation</li>
                </ul>
              </div>
              <div className="panel-right">
                <img src="img/SIDEEVENT-15APRIL/IMG_0344.jpg" alt="Side Event" />
                <span className="popup-trigger" onClick={() => handlePopupOpen('gallery')}>GALLERY</span>
              </div>
            </div>
          </Accordion>
          <Accordion title="Plenary Session II: Confronting Plastic Pollution" index="2">
            <div className="flex">
              <div className="panel-left">
                <p>The 9th Our Ocean Conference hosted 469 commitments from representatives of 119 countries, organizations, and businesses worldwide. During Plenary Session II: Confronting Plastic Pollution, Evi Lazou-Laskaridis, Chairman of the Athanasios C. Laskaridis Charitable Foundation, announced a significant financial commitment of €10 million to combat plastic pollution.</p>
              </div>
              <div className="panel-right">
                <img src="img/screenshot2.png" alt="Side Event" />
                <span className="popup-trigger" onClick={() => handlePopupOpen('video')}>PLAY VIDEO</span>
              </div>
            </div>
          </Accordion>
          <Accordion title="Side Event: Marine Pollution: Bridging Science and Policymaking" index="3">
            <div className="flex">
              <div className="panel-left">
                <p>Marine litter harms the environment and the health of ecosystems. The side event, “Marine Pollution: Bridging Science and Policymaking,” emphasized the necessity of understanding our connection to the sea, the severity of the problem, and the importance of partnerships and scientific data collection for preserving planetary health.</p>
                <ul> Speakers:
                  <li><strong>H.A.H Ecumenical Patriarch Bartholomew</strong></li>
                  <li><strong>Evi Lazou-Laskaridis</strong>, Chairman, Athanasios C. Laskaridis Charitable Foundation</li>
                  <li><strong>Maria Damanaki</strong>, Independent Advisor on Climate-Oceans, EU Commissioner for Maritime & Fisheries (2010-2014)</li>
                  <li><strong>George Papatheodorou</strong>, Professor, Geology Department and Dean of the School of Natural Sciences, University of Patras</li>
                  <li><strong>Cyrill Gutsch</strong>, CEO/Founder, Parley for the Oceans</li>
                  <li><strong>Maxine Burkett</strong>, Assistant Director for Climate Ocean and Equity, White House Office of Science & Technology Policy</li>
                </ul>
              </div>
              <div className="panel-right">
                <img src="img/SIDEEVENT-17 APRIL(1)/gxg_1309_53662440108_o.jpg" alt="Side Event" />
                <span className="popup-trigger" onClick={() => handlePopupOpen('both')}>GALLERY & VIDEO</span>
              </div>
            </div>
          </Accordion>
          <Accordion title="Waves of Cinema: A Festival of Oceanic Films – Fishing For Litter Documentary" index="4">
            <div className="flex">
              <div className="panel-left">
                <p>The “Fishing For Litter” documentary, a collaboration between Sotiris Danezis and the Athanasios C. Laskaridis Charitable Foundation, was featured at the “Our Ocean Film Festival” during the conference. The documentary explores the lives of four veteran fishermen, depicting the significant changes in the Aegean Sea and highlighting the pioneering “Fishing for Litter” initiative.</p>
              </div>
              <div className="panel-right">
                <img src="img/screenshot3.png"></img>
                <span className="popup-trigger" onClick={() => handlePopupOpen('video-large')}>PLAY VIDEO</span>
              </div>
            </div>
          </Accordion>
          <Gallery isOpen={isPopupOpen} contentType={popupContentType} onClose={handlePopupClose} />
      </div>
    </section>
  );
};

export default Commitments;
